import { Box, Button, Flex, Spinner } from '@chakra-ui/react';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { Icons } from '@texas/components/shared/Icons';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { EntityDirectoryEmptyState } from '@texas/components/shared/entityDirectory/EntityDirectoryEmptyState';
import { EntityDirectoryFilterBar } from '@texas/components/shared/entityDirectory/EntityDirectoryFilterBar';
import { EntityDirectoryPage } from '@texas/components/shared/entityDirectory/EntityDirectoryPage';
import { EntityDirectorySelectFilter } from '@texas/components/shared/entityDirectory/EntityDirectorySelectFilter';
import { EntityLifecycleTabs } from '@texas/components/shared/entityDirectory/EntityLifecycleTabs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateOfferModal } from './CreateOfferModal';
import { OfferOverviewTable } from './OfferOverviewTable';
import { useOffers } from './useOffers';

interface Props {
  variantId: number;
}

export function Offers({ variantId }: Props) {
  const { t } = useTranslation();
  const {
    activeLifecycle,
    branchId,
    branchOptions,
    error,
    lifecycleItems,
    loading,
    rows,
    searchInput,
    setActiveLifecycle,
    setBranchId,
    setSearchInput,
  } = useOffers(variantId);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  return (
    <BodyOverride>
      <EntityDirectoryPage
        actions={
          <Button
            leftIcon={<Icons.Plus boxSize={4} />}
            variant="texas-solid"
            onClick={() => setIsPreviewOpen(true)}
          >
            {t('offer.createDraftOffer')}
          </Button>
        }
        description={t('offer.overviewDescription')}
        headingLineHeight="38px"
        headingSize="32px"
        maxW="none"
        minH="calc(100dvh - 170px)"
        pb={32}
        px={0}
        py={0}
        title={t('offer.offers')}
        titleTextTransform="uppercase"
      >
        <EntityLifecycleTabs
          active={activeLifecycle}
          filters={
            <EntityDirectoryFilterBar py={3}>
              <Box flex="1 1 240px" maxW={{ base: 'full', lg: '340px' }}>
                <SearchFilterInput
                  style="entityDirectory"
                  value={searchInput}
                  onChange={setSearchInput}
                  placeholder={t('offer.searchPlaceholder')}
                />
              </Box>
              <EntityDirectorySelectFilter
                buttonProps={{ minW: '180px' }}
                options={branchOptions}
                placeholder={t('filter.allBranches')}
                value={branchId}
                onChange={setBranchId}
              />
            </EntityDirectoryFilterBar>
          }
          items={lifecycleItems}
          label={t('offer.lifecycle')}
          onChange={setActiveLifecycle}
        >
          {loading && (
            <Flex justify="center" py={12}>
              <Spinner />
            </Flex>
          )}
          {error && <ErrorDetails error={error} />}
          {!loading && !error && rows.length === 0 && (
            <EntityDirectoryEmptyState>
              {t('general.noResult')}
            </EntityDirectoryEmptyState>
          )}
          {!loading && !error && rows.length > 0 && (
            <OfferOverviewTable rows={rows} />
          )}
        </EntityLifecycleTabs>
      </EntityDirectoryPage>
      <CreateOfferModal
        variantId={variantId}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </BodyOverride>
  );
}
